import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { alpha, useTheme } from '@mui/material/styles';
import AssistantMarkdown from './AssistantMarkdown';
import AnswerFeedback from './AnswerFeedback';
import type { Turn } from './types';

/**
 * Fixed, deterministic notice rendered above a non-refusal answer that is not
 * grounded in the FAR/RFO. This is a HARDCODED frontend constant — it is NEVER
 * sourced from the response body (`turn.summary`/`turn.detail`). It is driven
 * purely by the deterministic `references_far` flag (with a citation-kind
 * fallback). See issues/20260629-ungrounded-answer-fallback_spec-implplan §5.4.
 */
export const NOT_FAR_NOTICE = 'This answer is not based on the FAR/RFO.';

interface Props {
  turn: Turn;
  workflowId: string | null;
  threadId: string | null;
  /**
   * Which chat surface this answer is rendered in. Drives the friendly label
   * attached to per-answer feedback ("Ask FAR" vs "Homepage chat"). Defaults
   * to the workflow Ask FAR popup.
   */
  source?: 'farchat' | 'homechat';
  /** Pending recovery has answer text but no grounding metadata. */
  feedbackRecovery?: boolean;
  /** Present only when the owning Home Chat surface received server edit authorization. */
  onEditTurn?: (turn: Turn) => void;
  /** Inline editor state remains owned by the server-authoritative Home Chat transcript. */
  editing?: {
    draft: string;
    onDraftChange: (draft: string) => void;
    onSave: () => void;
    onCancel: () => void;
    saving: boolean;
  } | null;
  /** A turn-local processing surface, used while an edited message is resent. */
  processingContent?: React.ReactNode;
  /** Server-issued Home Chat action for this answer, rendered with router choices. */
  trainingAction?: {
    card_id: string;
    state: 'pending' | 'error';
    error?: string | null;
    busy: boolean;
  };
  onSelectTrainingAction?: (cardId: string) => void;
}

/**
 * v2 single-bubble Q&A renderer.
 *
 * Layout (top to bottom, all inside ONE bubble):
 *   - Summary (always visible)
 *   - Divider
 *   - Detail toggle (collapsed by default)
 *   - Detail body (when expanded)
 *
 * Refusal turns render only the refusal text.
 */
const FarChatResponse: React.FC<Props> = ({
  turn,
  workflowId,
  source,
  feedbackRecovery = false,
  onEditTurn,
  editing = null,
  processingContent = null,
  trainingAction,
  onSelectTrainingAction,
}) => {
  const theme = useTheme();
  const [detailExpanded, setDetailExpanded] = useState(false);
  useEffect(() => {
    // While other controls are disabled, the full answer must remain readable.
    const update = (event?: Event) => {
      const turnId = event ? (event as CustomEvent<string>).detail : document.body.dataset.chatFeedbackPendingTurn;
      if (turnId && turnId === turn.turn_id) setDetailExpanded(true);
    };
    update();
    window.addEventListener('chat-feedback-pending-changed', update);
    return () => window.removeEventListener('chat-feedback-pending-changed', update);
  }, [turn.turn_id]);
  const showDetail = detailExpanded;

  // Friendly surface label for per-answer feedback (email + rate-limit bucket).
  const feedbackSurface = source === 'homechat' ? 'Homepage chat' : 'Ask FAR';
  // A Homepage answer report belongs in the Homepage - Chat Top Sources bucket,
  // while its friendly surface stays Homepage chat for the independent rate limit.
  const feedbackSource = source === 'homechat'
    ? { sourcePage: 'homepage' as const, sourceArea: 'chat' as const, sourceConfidence: 'high' as const }
    : undefined;
  const answerFeedback = (
    <AnswerFeedback
      turn={turn}
      surface={feedbackSurface}
      contextId={workflowId ?? null}
      source={feedbackSource}
    />
  );

  // Live answers wake the single app-wide gate; historical answers carry no marker.
  const promptId = turn.feedbackRequest?.promptId;
  useEffect(() => {
    if (!promptId) return;
    window.dispatchEvent(new CustomEvent('chat-feedback-required', { detail: {
      prompt_id: promptId,
      origin: 'live_answer',
      surface: source === 'homechat' ? 'homechat' : 'farchat',
      turn_id: turn.turn_id,
      context_id: workflowId,
      question: turn.user_msg,
      answer: turn.summary + (turn.detail ? `\n\n${turn.detail}` : ''),
    } }));
  }, [promptId, source, turn.turn_id, turn.user_msg, turn.summary, turn.detail, workflowId]);

  // Deterministic "not based on FAR/RFO" flag. Primary signal is the explicit
  // backend `references_far` flag; the citation-kind check is a defensive
  // fallback for turns that predate the flag. NEVER derived from answer prose.
  const hasVerifiedPolicy = turn.citations.some(
    (c) => c.kind === 'policy' && c.source === 'policy' && c.verified && !!c.url && !!c.quote,
  );
  const notBasedOnFar = !feedbackRecovery && !turn.training_guidance && !hasVerifiedPolicy && (
    turn.references_far === false ||
    !turn.citations.some((c) => c.kind === 'far' || c.kind === 'legacy_far')
  );

  // User bubble — rendered above every assistant bubble so a v2 Turn
  // record carries the question + answer pair for both reload and live
  // post paths.
  const userBubble = turn.user_msg ? (
    <>
      <Box
        data-testid="farchat-user-bubble"
        data-turn-id={turn.turn_id}
        data-role="user"
        sx={{
          alignSelf: 'flex-end',
          maxWidth: '85%',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          whiteSpace: 'pre-wrap',
          fontSize: '0.9rem',
          boxShadow: 0,
          wordBreak: 'break-word',
        }}
      >
        {editing ? (
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Edit message"
            value={editing.draft}
            onChange={(event) => editing.onDraftChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                editing.onCancel();
              }
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                editing.onSave();
              }
            }}
            inputProps={{ 'aria-label': 'Edit message' }}
            sx={{
              '& .MuiInputBase-root': { color: 'primary.contrastText' },
              '& .MuiInputLabel-root': { color: 'primary.contrastText' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.contrastText' },
            }}
          />
        ) : turn.user_msg}
      </Box>
      {editing ? (
        <Box sx={{ alignSelf: 'flex-end', display: 'flex', gap: 1, mt: -0.25 }}>
          <Button size="small" variant="text" color="inherit" onClick={editing.onCancel} disabled={editing.saving}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={editing.onSave}
            disabled={editing.saving || !editing.draft.trim() || editing.draft.trim() === turn.user_msg}
          >
            Save and resend
          </Button>
        </Box>
      ) : turn.editable === true && onEditTurn ? (
        <Button
          size="small"
          variant="text"
          onClick={() => onEditTurn(turn)}
          aria-label="Edit message"
          sx={{ alignSelf: 'flex-end', mt: -0.75, minWidth: 0, textTransform: 'none' }}
        >
          Edit
        </Button>
      ) : null}
      {/* Clarified callout — rendered directly below the original query
          when the clarification gate fired and the user picked/typed a
          rewrite that differs from their original. Visually distinct
          (callout background/border) and labelled "Clarified:". Null /
          legacy turns render nothing extra. See clarification-step §6. */}
      {turn.clarification ? (
        <Box
          data-testid="farchat-clarified-callout"
          data-turn-id={turn.turn_id}
          className="farchat-clarified-callout"
          sx={{
            alignSelf: 'flex-end',
            maxWidth: '85%',
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.light, 0.45),
            color: 'text.primary',
            border: '1px dashed',
            borderColor: alpha(theme.palette.info.main, 0.55),
            fontSize: '0.8rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
            Clarified:
          </Box>
          {turn.clarification}
        </Box>
      ) : null}
      {/* Focused-on callout — rendered directly below the original query
          (and any "Clarified:" callout) when the post-reader topic-focus
          gate fired and the user picked a focus. Visually distinct
          (callout background/border) and labelled "Focused on:". Null /
          empty / legacy turns render nothing extra. See post-reader spec §9. */}
      {turn.topic_focus ? (
        <Box
          data-testid="farchat-focused-callout"
          data-turn-id={turn.turn_id}
          className="farchat-focused-callout"
          sx={{
            alignSelf: 'flex-end',
            maxWidth: '85%',
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.light, 0.5),
            color: 'text.primary',
            border: '1px dashed',
            borderColor: alpha(theme.palette.success.main, 0.55),
            fontSize: '0.8rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
            Focused on:
          </Box>
          {turn.topic_focus}
        </Box>
      ) : null}
    </>
  ) : null;

  // An edited Home Chat request retains its original transcript position.
  // Do not show the previous answer or create a second in-flight message at
  // the end of the transcript while the server prepares its replacement.
  if (processingContent) {
    return <>{userBubble}{processingContent}</>;
  }

  // Refusal: render only the refusal text.
  if (turn.is_refusal) {
    return (
      <>
        {userBubble}
        <Box
          data-role="assistant"
          data-testid="farchat-response-bubble"
          data-turn-id={turn.turn_id}
          data-refusal="true"
          sx={{
            alignSelf: 'flex-start',
            maxWidth: '85%',
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.light, 0.7),
            color: 'text.primary',
            fontSize: '0.9rem',
            boxShadow: 1,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
            wordBreak: 'break-word',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box>{turn.refusal_text || turn.summary}</Box>
        </Box>
        {answerFeedback}
        <Box data-chat-feedback-turn={turn.turn_id} />
      </>
    );
  }

  return (
    <>
      {userBubble}
      <Box
        data-role="assistant"
          data-testid="farchat-response-bubble"
        data-turn-id={turn.turn_id}
        sx={{
          alignSelf: 'flex-start',
          maxWidth: '85%',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: 'surfaceMuted',
          color: 'text.primary',
          fontSize: '0.9rem',
          boxShadow: 1,
          wordBreak: 'break-word',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Not-FAR-grounded notice — fixed constant, rendered above the
            summary when the answer is not based on the FAR/RFO. The text is
            ALWAYS `NOT_FAR_NOTICE` (never `turn.summary`/`turn.detail`). */}
        {notBasedOnFar && (
          <Box
            data-testid="not-far-notice"
            sx={{
              px: 1,
              py: 0.75,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.info.light, 0.25),
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.info.main, 0.4)}`,
              fontSize: '0.8rem',
              fontWeight: 500,
            }}
          >
            {NOT_FAR_NOTICE}
          </Box>
        )}

        {/* Summary — always visible, no collapse toggle, no header label. */}
        <Box data-testid="response-summary" data-chat-answer-content>
          <AssistantMarkdown text={turn.summary} citations={turn.citations} />
        </Box>

        {trainingAction && (
          <Box data-testid="training-action-choice" sx={{ display: 'grid', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              disabled={trainingAction.state !== 'pending' || trainingAction.busy || !onSelectTrainingAction}
              onClick={() => onSelectTrainingAction?.(trainingAction.card_id)}
              sx={{ alignItems: 'flex-start', justifyContent: 'flex-start', textAlign: 'left', px: 1.5, py: 1.25, textTransform: 'none' }}
            >
              Open training
            </Button>
            {trainingAction.state === 'error' && (
              <Box role="status" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                {trainingAction.error || 'Training is unavailable'}
              </Box>
            )}
          </Box>
        )}

        {/* Divider before detail. */}
        <Divider data-testid="section-divider-detail" />

        {/* Detail — collapsed by default. */}
        <Box data-testid="response-detail" data-chat-answer-content>
          <Button
            size="small"
            variant="text"
            aria-expanded={showDetail}
            onClick={() => setDetailExpanded((v) => !v)}
            startIcon={
              showDetail ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )
            }
            sx={{
              textTransform: 'none',
              p: 0,
              minWidth: 0,
              fontSize: '0.85rem',
              fontWeight: 500,
              color: 'text.primary',
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            {showDetail ? 'Show Less' : 'Show More'}
          </Button>
          {showDetail && turn.detail && (
            <Box sx={{ mt: 0.75 }} data-testid="response-detail-body">
              <AssistantMarkdown
                text={turn.detail}
                citations={turn.citations}
              />
            </Box>
          )}
        </Box>
      </Box>
      {answerFeedback}
      <Box data-chat-feedback-turn={turn.turn_id} />
    </>
  );
};

export default FarChatResponse;
